import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../components/ui/table';
import { formatCurrency } from '../utils/formatters';
import { UploadedSplitsSection } from './UploadedSplitsSection';

export const ManageSplitsDialog = ({
  open,
  onOpenChange,
  splitParentsLoading,
  splitParents,
  splitDiagnostics,
  splitDiagLoadingId,
  runSplitDiagnostics,
  reassignSplitChildren,
  unsplitFromManageDialog,
  reconInvestorFilter,
  headers,
  onUploadedSplitUndone,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="manage-splits-dialog">
      <DialogHeader>
        <DialogTitle>Manage Splits</DialogTitle>
        <DialogDescription>
          Undo splits of uploaded transactions (first section) and of bank lines (second section).
        </DialogDescription>
      </DialogHeader>
      <UploadedSplitsSection open={open} headers={headers} reconInvestorFilter={reconInvestorFilter} onUndone={onUploadedSplitUndone} />
      <h3 className="font-semibold text-sm pt-4">Bank line splits</h3>
      <div className="py-2">
        {splitParentsLoading ? (
          <div className="py-8 text-center text-slate-500">Loading&hellip;</div>
        ) : splitParents.length === 0 ? (
          <div className="py-8 text-center text-slate-500">No split bank lines.</div>
        ) : (
          <div className="border rounded overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Parent amount</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Investor / RP</TableHead>
                  <TableHead className="text-center">Children</TableHead>
                  <TableHead className="text-center">Allocated</TableHead>
                  <TableHead className="text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {splitParents.map(p => {
                  const diag = splitDiagnostics[p.id];
                  return (
                  <React.Fragment key={p.id}>
                  <TableRow data-testid={`split-parent-row-${p.id}`}>
                    <TableCell className="text-xs">{p.transaction_date || '—'}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(p.amount)}</TableCell>
                    <TableCell className="max-w-[280px] truncate text-xs" title={p.description}>{p.description || '—'}</TableCell>
                    <TableCell className="text-xs">
                      <div className="font-mono">{(p.investor_id || '—').toString().slice(0, 8)}…</div>
                      <div className="text-slate-500">{p.risk_participant || ''}</div>
                    </TableCell>
                    <TableCell className="text-center text-xs">
                      {p.child_count}
                      {p.child_amounts && p.child_amounts.length > 0 && (
                        <div className="text-slate-500 font-mono text-[10px]">
                          {p.child_amounts.map(a => formatCurrency(a)).join(' + ')}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className={`text-center text-xs ${p.children_allocated_count > 0 ? 'text-amber-700 font-medium' : ''}`}>
                      {p.children_allocated_count}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => runSplitDiagnostics(p.id)}
                          disabled={splitDiagLoadingId === p.id}
                          data-testid={`diagnose-btn-${p.id}`}
                          title="Show why each child is/isn't rendering on the recon page"
                        >
                          {splitDiagLoadingId === p.id ? '…' : 'Diagnose'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => reassignSplitChildren(p.id)}
                          disabled={p.children_allocated_count > 0 || !reconInvestorFilter || reconInvestorFilter === 'all'}
                          className="border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-40"
                          data-testid={`reassign-children-btn-${p.id}`}
                          title={
                            (!reconInvestorFilter || reconInvestorFilter === 'all')
                              ? 'Select an investor on the recon page first, then click to reassign these children to that investor.'
                              : 'Reassign every child to the currently-selected recon investor.'
                          }
                        >
                          Reassign
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!p.can_unsplit}
                          onClick={() => unsplitFromManageDialog(p.id)}
                          className="text-red-700 border-red-300 hover:bg-red-50 disabled:opacity-40"
                          data-testid={`unsplit-btn-${p.id}`}
                          title={p.can_unsplit ? 'Delete children and restore parent' : 'Cannot unsplit — a child has been allocated. Unallocate first.'}
                        >
                          Unsplit
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  {diag && (
                    <TableRow data-testid={`split-diag-row-${p.id}`}>
                      <TableCell colSpan={7} className="bg-slate-50 p-3">
                        <div className="text-xs space-y-2">
                          <div className="font-semibold text-slate-700">
                            Parent lives in: <span className="font-mono">{diag.found_in || '—'}</span>
                            {' · '}
                            Hint: <span className="italic text-slate-600">{diag.hint}</span>
                          </div>
                          {diag.runtime_trace && (
                            <div className="rounded border border-blue-300 bg-blue-50 p-2 text-blue-900">
                              <div className="font-semibold">Runtime pipeline trace (investor filter = {diag.runtime_trace.investor_id})</div>
                              <div>Resolved candidates: <span className="font-mono">{JSON.stringify(diag.runtime_trace.candidates)}</span></div>
                              {diag.runtime_trace.resolved_user && (
                                <div>User: <b>{diag.runtime_trace.resolved_user.name}</b> · id=<span className="font-mono">{diag.runtime_trace.resolved_user.id}</span> · rp=<span className="font-mono">{diag.runtime_trace.resolved_user.rp}</span></div>
                              )}
                              <div>{diag.runtime_trace.summary?.children_that_would_appear ?? 0} / {diag.runtime_trace.summary?.children_total ?? 0} children would appear in <code>unmatched_bank</code> for this investor.</div>
                            </div>
                          )}
                          {diag.children && diag.children.length > 0 ? (
                            <div className="border rounded overflow-hidden bg-white">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className="text-[10px]">Child id</TableHead>
                                    <TableHead className="text-[10px] text-right">Amount</TableHead>
                                    <TableHead className="text-[10px]">Date</TableHead>
                                    <TableHead className="text-[10px]">Collection</TableHead>
                                    <TableHead className="text-[10px]">Source</TableHead>
                                    <TableHead className="text-[10px]">Investor id</TableHead>
                                    <TableHead className="text-[10px]">Investor UUID</TableHead>
                                    <TableHead className="text-[10px]">Import st.</TableHead>
                                    <TableHead className="text-[10px]">Recon st.</TableHead>
                                    <TableHead className="text-[10px] text-center">Alloc</TableHead>
                                    <TableHead className="text-[10px] text-center">Static verdict</TableHead>
                                    <TableHead className="text-[10px] text-center">Runtime verdict</TableHead>
                                    <TableHead className="text-[10px]">Reasons</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {diag.children.map(c => (
                                    <TableRow key={c.id}>
                                      <TableCell className="text-[10px] font-mono">{(c.id || '').slice(0, 8)}…</TableCell>
                                      <TableCell className="text-[10px] font-mono text-right">{formatCurrency(c.amount)}</TableCell>
                                      <TableCell className="text-[10px]">{c.transaction_date || '—'}</TableCell>
                                      <TableCell className="text-[10px]">{c.collection?.replace('investor_transactions', 'it')}</TableCell>
                                      <TableCell className="text-[10px] font-mono">{c.source || '—'}</TableCell>
                                      <TableCell className="text-[10px] font-mono">{c.investor_id || '—'}</TableCell>
                                      <TableCell className="text-[10px] font-mono">{(c.investor_uuid || '—').toString().slice(0, 12)}</TableCell>
                                      <TableCell className="text-[10px]">{c.import_status || '—'}</TableCell>
                                      <TableCell className="text-[10px]">{c.reconciliation_status || '—'}</TableCell>
                                      <TableCell className="text-[10px] text-center">{c.allocation_count}</TableCell>
                                      <TableCell className="text-[10px] text-center">
                                        {c.would_show_on_recon
                                          ? <span className="text-green-700 font-semibold">YES</span>
                                          : <span className="text-red-700 font-semibold">NO</span>}
                                      </TableCell>
                                      <TableCell className="text-[10px] text-center">
                                        {c.pipeline_trace
                                          ? (c.pipeline_trace.final_verdict_would_appear_in_unmatched_bank
                                              ? <span className="text-green-700 font-semibold">YES</span>
                                              : <span className="text-red-700 font-semibold">NO</span>)
                                          : <span className="text-slate-400">n/a</span>}
                                      </TableCell>
                                      <TableCell className="text-[10px]">
                                        {(c.reasons || []).join('; ')}
                                        {c.pipeline_trace?.reason && (
                                          <div className="text-red-700 mt-1">{c.pipeline_trace.reason}</div>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          ) : (
                            <div className="text-red-700 text-xs">No children found for this parent.</div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
